import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ReactSortable } from 'react-sortablejs';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import {
  Toggle,
  Input,
  Textarea,
  AttachmentUpload,
  Select,
} from 'components/FormFields';
import { DragIndicatorIcon } from 'components/Icon/DragIndicatorIcon';
import { Modal } from 'components/Modal';
import useTheme from 'hooks/useTheme';
import {
  Container,
  Header,
  HeaderTitle,
  SectionHeader,
  SectionIcon,
  SectionTitle,
  HelperText,
  ListContainer,
  SortableContainer,
  InfoCardWrapper,
  InfoCard,
  DragHandle,
  InfoIcon,
  InfoContent,
  InfoName,
  ContentPills,
  Pill,
  PreviewButton,
  Actions,
  MobileBreak,
  LoadingContainer,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  DetailsPanel,
  DetailsContent,
  DetailsGrid,
  DetailItem,
  DetailLabel,
  DetailLink,
  DetailText,
  MediaPreview,
  MediaLabel,
  NoContent,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalImage,
  ModalPdfFrame,
  ModalVideoFrame,
  ModalLabel,
  ClickableMediaItem,
  ClickablePdfPreview,
  ClickableVideoPreview,
  SourceBadge,
  DeleteButton,
  ReorderToggle,
  ReorderHint,
  ReorderCard,
  ReorderDragStrip,
  ReorderCardContent,
  ReorderOrderNum,
  ReorderNameArea,
  ReorderName,
  ReorderSub,
  ReorderDoneButton,
} from './StyledComponents';

// GraphQL query to get all general info categories
const GET_ALL_INFOS = gql`
  query GetAllInfos {
    getAllInfos {
      count
      results {
        id
        name
        icon
      }
    }
  }
`;

// Modal styled components
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid
    ${({ theme }) => (theme?.name === 'dark' ? '#334155' : '#e2e8f0')};
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#1e293b' : '#ffffff'};
`;

const ModalHeaderTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0f172a')};
  margin: 0;
`;

const ModalCloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'};
  color: ${({ theme }) => (theme?.name === 'dark' ? '#94a3b8' : '#64748b')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.08)'};
    color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0f172a')};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  text-align: left;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#1e293b' : '#ffffff'};
`;

const HelperBox = styled.div`
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(59, 130, 246, 0.1)' : '#eff6ff'};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#bfdbfe'};
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 24px;
`;

const HelperBoxTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#60a5fa' : '#3b82f6')};
  font-size: 13px;
  font-weight: 600;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

const HelperBoxText = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#94a3b8' : '#475569')};

  strong {
    color: ${({ theme }) => (theme?.name === 'dark' ? '#e2e8f0' : '#1e293b')};
    font-weight: 600;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FormGroupFullWidth = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid
    ${({ theme }) => (theme?.name === 'dark' ? '#334155' : '#e2e8f0')};
`;

const ButtonWrapper = styled.div`
  width: auto;

  button {
    width: auto;
    padding: 10px 24px;
  }
`;

const FieldLabel = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0f172a')};
`;

// Icons
const TrackIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const SeriesIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

const InfoPanelIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const ChevronIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const PdfIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const VideoIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
);

const LinkIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const HelpIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const EventIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// Helper function to get content types for an info panel
const getContentTypes = (info) => {
  const types = [];
  if (info.text) types.push('text');
  if (info.link) types.push('link');
  if (info.pdf_id || info.pdf_url) types.push('pdf');
  if (info.image_id || info.image_url) types.push('image');
  if (info.video_url) types.push('video');
  return types;
};

// Content type labels
const contentTypeLabels = {
  text: 'Text',
  link: 'Link',
  pdf: 'PDF',
  image: 'Image',
  video: 'Video',
};

// Helper to convert video URLs to embed URLs
const getVideoEmbedUrl = (url) => {
  if (!url) return null;

  // Vimeo
  const vimeoMatch = url.match(
    /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/
  );
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  // YouTube
  const youtubeMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/
  );
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }

  // Already an embed URL or other format
  return url;
};

// Media Modal Component
const MediaModal = ({ media, onClose }) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!media) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContent>
        <ModalCloseButton onClick={onClose} title="Close (Esc)">
          <CloseIcon />
        </ModalCloseButton>

        {media.type === 'image' && (
          <>
            <ModalImage src={media.url} alt={media.label || 'Image'} />
            {media.label && <ModalLabel>{media.label}</ModalLabel>}
          </>
        )}

        {media.type === 'pdf' && (
          <>
            <ModalPdfFrame src={media.url} title="PDF Preview" />
            <ModalLabel>PDF Document</ModalLabel>
          </>
        )}

        {media.type === 'video' && (
          <>
            <ModalVideoFrame
              src={getVideoEmbedUrl(media.url)}
              title="Video"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
            <ModalLabel>Video</ModalLabel>
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

// Generate unique IDs for new event infos
let eventInfoIdCounter = 0;
const generateEventInfoId = () => {
  eventInfoIdCounter += 1;
  return `event-info-${Date.now()}-${eventInfoIdCounter}`;
};

// Add Event Info Modal Component
const AddEventInfoModal = ({ isOpen, onClose, onSave, editingInfo }) => {
  const theme = useTheme();
  const isDark = theme?.name === 'dark';

  // Fetch all general info categories
  const { data: infosData, loading: infosLoading } = useQuery(GET_ALL_INFOS, {
    skip: !isOpen,
  });

  const [selectedInfoId, setSelectedInfoId] = useState('');
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [logoAttachments, setLogoAttachments] = useState([]);
  const [imageAttachments, setImageAttachments] = useState([]);
  const [pdfAttachments, setPdfAttachments] = useState([]);

  // Build options for the select dropdown
  const infoOptions = useMemo(() => {
    if (!infosData?.getAllInfos?.results) return [];
    return infosData.getAllInfos.results.map((info) => ({
      value: info.id,
      label: info.name,
      icon: info.icon,
    }));
  }, [infosData]);

  // Get the selected info object from the ID
  const selectedInfo = useMemo(() => {
    if (!selectedInfoId || !infoOptions.length) return null;
    return (
      infoOptions.find((opt) => String(opt.value) === String(selectedInfoId)) ||
      null
    );
  }, [selectedInfoId, infoOptions]);

  // Reset form when modal opens/closes or when editing different info
  useEffect(() => {
    if (isOpen && editingInfo) {
      // Editing existing info
      const infoId = editingInfo.info?.id || editingInfo.info?.value || '';
      setSelectedInfoId(String(infoId));
      setText(editingInfo.text || '');
      setLink(editingInfo.link || '');
      setVideoUrl(editingInfo.video_url || '');
      // Convert existing media to attachment format
      if (editingInfo.logo) {
        setLogoAttachments([
          {
            id: editingInfo.logo_id || 'existing-logo',
            name: 'Logo',
            size: 0,
            sizeFormatted: '',
            url: editingInfo.logo,
            type: 'image/*',
            isImage: true,
          },
        ]);
      } else {
        setLogoAttachments([]);
      }
      if (editingInfo.image_url) {
        setImageAttachments([
          {
            id: editingInfo.image_id || 'existing-image',
            name: 'Image',
            size: 0,
            sizeFormatted: '',
            url: editingInfo.image_url,
            type: 'image/*',
            isImage: true,
          },
        ]);
      } else {
        setImageAttachments([]);
      }
      if (editingInfo.pdf_url) {
        setPdfAttachments([
          {
            id: editingInfo.pdf_id || 'existing-pdf',
            name: 'PDF',
            size: 0,
            sizeFormatted: '',
            url: editingInfo.pdf_url,
            type: 'application/pdf',
            isPDF: true,
          },
        ]);
      } else {
        setPdfAttachments([]);
      }
    } else if (isOpen) {
      // Creating new info - reset form
      setSelectedInfoId('');
      setText('');
      setLink('');
      setVideoUrl('');
      setLogoAttachments([]);
      setImageAttachments([]);
      setPdfAttachments([]);
    }
  }, [isOpen, editingInfo]);

  const handleSubmit = () => {
    if (!selectedInfo) return;

    const infoData = {
      id: editingInfo?.id || generateEventInfoId(),
      info: selectedInfo, // Store the selected info category { value: id, label: name }
      name: selectedInfo.label, // Use the category name as the display name
      text: text.trim() || null,
      link: link.trim() || null,
      video_url: videoUrl.trim() || null,
      logo: logoAttachments[0]?.url || null,
      logo_id: logoAttachments[0]?.id || null,
      image_url: imageAttachments[0]?.url || null,
      image_id: imageAttachments[0]?.id || null,
      pdf_url: pdfAttachments[0]?.url || null,
      pdf_id: pdfAttachments[0]?.id || null,
      source: 'event',
      isEventInfo: true,
    };

    onSave(infoData);
    onClose();
  };

  // Modal style for dark/light mode
  const modalStyle = {
    backgroundColor: isDark ? '#1e293b' : '#ffffff',
    border: isDark ? '1px solid #334155' : 'none',
  };

  // Custom themed header
  const customHeader = (
    <ModalHeader>
      <ModalHeaderTitle>
        {editingInfo ? 'Edit Event Info' : 'Add Event Info'}
      </ModalHeaderTitle>
      <ModalCloseBtn onClick={onClose}>
        <CloseIcon />
      </ModalCloseBtn>
    </ModalHeader>
  );

  return (
    <Modal
      isVisible={isOpen}
      hideModal={onClose}
      maxWidth={750}
      modalStyle={modalStyle}
      header={customHeader}
    >
      <ModalBody>
        <HelperBox>
          <HelperBoxTitle>
            <HelpIcon />
            How Info Panels Work
          </HelperBoxTitle>
          <HelperBoxText>
            You can create an info panel with <strong>any combination</strong>{' '}
            of Text, Video, Image, or PDF. However, if you add a{' '}
            <strong>Link URL</strong>, that will be the only action available
            &mdash; tapping the info panel in the mobile app will simply open
            that link.
          </HelperBoxText>
        </HelperBox>

        <FormGroupFullWidth>
          <Select
            label="General Info Category"
            required
            placeholder={infosLoading ? 'Loading...' : 'Select a category'}
            value={selectedInfoId}
            onChange={(e) => setSelectedInfoId(e.target.value)}
            options={infoOptions}
          />
        </FormGroupFullWidth>

        <FormGroupFullWidth>
          <Textarea
            label="Text Content"
            placeholder="Enter text content (optional)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
          />
        </FormGroupFullWidth>

        <FormGrid>
          <FormGroup>
            <Input
              label="Link URL"
              placeholder="https://example.com"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              helperText="If set, this becomes the only action"
            />
          </FormGroup>

          <FormGroup>
            <Input
              label="Video URL"
              placeholder="YouTube or Vimeo URL"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              helperText="Supports YouTube and Vimeo links"
            />
          </FormGroup>

          <FormGroup>
            <FieldLabel>Logo</FieldLabel>
            <AttachmentUpload
              attachments={logoAttachments}
              onAttachmentsChange={setLogoAttachments}
              maxFiles={1}
              channel="SMS"
            />
          </FormGroup>

          <FormGroup>
            <FieldLabel>Image</FieldLabel>
            <AttachmentUpload
              attachments={imageAttachments}
              onAttachmentsChange={setImageAttachments}
              maxFiles={1}
              channel="SMS"
            />
          </FormGroup>

          <FormGroup>
            <FieldLabel>PDF Document</FieldLabel>
            <AttachmentUpload
              attachments={pdfAttachments}
              onAttachmentsChange={setPdfAttachments}
              maxFiles={1}
              channel="SMS"
            />
          </FormGroup>
        </FormGrid>

        <ButtonRow>
          <ButtonWrapper>
            <ButtonV2 type="button" onClick={onClose}>
              Cancel
            </ButtonV2>
          </ButtonWrapper>
          <ButtonWrapper>
            <ButtonV2
              elevated
              type="button"
              onClick={handleSubmit}
              disabled={!selectedInfo}
            >
              {editingInfo ? 'Save Changes' : 'Add Info'}
            </ButtonV2>
          </ButtonWrapper>
        </ButtonRow>
      </ModalBody>
    </Modal>
  );
};

// State is lifted to parent (EditEventV2) so it persists across tab switches
const FanInfoTab = ({
  event,
  loading,
  eventInfos = {
    trackInfos: [],
    seriesInfos: [],
    eventInfos: [],
    allInfos: [],
  },
  onEventInfosChange,
}) => {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [modalMedia, setModalMedia] = useState(null);
  const [addInfoModalOpen, setAddInfoModalOpen] = useState(false);
  const [editingEventInfo, setEditingEventInfo] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);

  // Get track infos from the event's track (for computing disabled items)
  const trackInfos = useMemo(() => {
    return event?.track?.infos || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.track?.infos]);

  // Get series infos from all event's series (for computing disabled items)
  const seriesInfos = useMemo(() => {
    const series = event?.series || [];
    // Flatten all series infos and add series metadata
    return series.flatMap((s) =>
      (s.infos || []).map((info) => ({
        ...info,
        seriesId: s.id,
        seriesName: s.name,
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.series]);

  // Use the enabled items from parent props (allInfos contains track, series, and event infos in order)
  const enabledItems = eventInfos.allInfos || [];

  // Compute disabled track items (track infos that are not in enabledItems)
  const disabledTrackItems = useMemo(() => {
    // Filter by source='track' to get track-specific enabled category IDs
    const enabledTrackCategoryIds = new Set(
      enabledItems
        .filter((i) => i.source === 'track')
        .map((i) => i.info?.id)
        .filter(Boolean)
    );
    return trackInfos
      .filter((info) => !enabledTrackCategoryIds.has(info.info?.id))
      .map((info) => ({
        ...info,
        enabled: false,
        source: 'track',
        trackInfo: info,
      }));
  }, [trackInfos, enabledItems]);

  // Compute disabled series items (series infos that are not in enabledItems)
  const disabledSeriesItems = useMemo(() => {
    // Filter by source='series' to get series-specific enabled category IDs
    const enabledSeriesCategoryIds = new Set(
      enabledItems
        .filter((i) => i.source === 'series')
        .map((i) => i.info?.id)
        .filter(Boolean)
    );
    return seriesInfos
      .filter((info) => !enabledSeriesCategoryIds.has(info.info?.id))
      .map((info) => ({
        ...info,
        enabled: false,
        source: 'series',
        seriesInfo: info,
      }));
  }, [seriesInfos, enabledItems]);

  // Helper to notify parent of changes
  const updateParent = (newEnabledItems) => {
    const trackInfosEnabled = newEnabledItems.filter(
      (i) => i.source === 'track'
    );
    const seriesInfosEnabled = newEnabledItems.filter(
      (i) => i.source === 'series'
    );
    const eventInfosList = newEnabledItems.filter(
      (i) => i.source === 'event' || i.isEventInfo
    );
    onEventInfosChange({
      trackInfos: trackInfosEnabled,
      seriesInfos: seriesInfosEnabled,
      eventInfos: eventInfosList,
      allInfos: newEnabledItems,
    });
  };

  // Handle toggle change for track/series infos
  // Uses category ID (info.id) for matching, not junction record ID
  const handleToggle = (categoryId, source) => {
    // Check if it's currently enabled or disabled (match by category ID)
    const enabledItem = enabledItems.find(
      (i) => i.info?.id === categoryId && i.source === source
    );

    if (enabledItem) {
      // Currently enabled - remove from enabled list
      const newEnabledItems = enabledItems.filter(
        (i) => !(i.info?.id === categoryId && i.source === source)
      );
      updateParent(newEnabledItems);
    } else {
      // Currently disabled - add to enabled list (at the end)
      const disabledItem =
        source === 'track'
          ? disabledTrackItems.find((i) => i.info?.id === categoryId)
          : disabledSeriesItems.find((i) => i.info?.id === categoryId);
      if (disabledItem) {
        const newEnabledItems = [
          ...enabledItems,
          { ...disabledItem, enabled: true },
        ];
        updateParent(newEnabledItems);
      }
    }
  };

  // Handle expand/collapse
  const handleToggleExpand = (infoId) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(infoId)) {
        newSet.delete(infoId);
      } else {
        newSet.add(infoId);
      }
      return newSet;
    });
  };

  // Handle reorder via drag and drop
  const handleSetList = (newList) => {
    // Check if order actually changed by comparing IDs
    const oldIds = enabledItems.map((i) => i.id).join(',');
    const newIds = newList.map((i) => i.id).join(',');

    // Skip if no change (this happens on initial mount)
    if (oldIds === newIds) {
      return;
    }

    // Update the enabled items with the new order
    updateParent(newList);
  };

  // Handle adding/editing event info
  const handleSaveEventInfo = (infoData) => {
    if (editingEventInfo) {
      // Update existing in enabledItems
      const newEnabledItems = enabledItems.map((item) =>
        item.id === infoData.id ? { ...infoData, enabled: true } : item
      );
      updateParent(newEnabledItems);
    } else {
      // Add new event info to the end of enabled items
      const newEnabledItems = [...enabledItems, { ...infoData, enabled: true }];
      updateParent(newEnabledItems);
    }
    setEditingEventInfo(null);
  };

  // Handle deleting event info
  const handleDeleteEventInfo = (infoId) => {
    const newEnabledItems = enabledItems.filter((item) => item.id !== infoId);
    updateParent(newEnabledItems);
  };

  // Handle editing event info
  const handleEditEventInfo = (info) => {
    setEditingEventInfo(info);
    setAddInfoModalOpen(true);
  };

  // Open media modal
  const openMedia = useCallback((type, url, label) => {
    setModalMedia({ type, url, label });
  }, []);

  // Close media modal
  const closeMedia = useCallback(() => {
    setModalMedia(null);
  }, []);

  if (loading) {
    return (
      <Card>
        <LoadingContainer>Loading fan info...</LoadingContainer>
      </Card>
    );
  }

  const hasAnyContent =
    trackInfos.length > 0 || seriesInfos.length > 0 || enabledItems.length > 0;

  return (
    <Container>
      <Card>
        <Header>
          <HeaderTitle>Fan Info</HeaderTitle>
          <div style={{ display: 'inline-block' }}>
            <ButtonV2
              type="button"
              elevated
              onClick={() => {
                setEditingEventInfo(null);
                setAddInfoModalOpen(true);
              }}
              style={{ width: 'auto', padding: '10px 20px' }}
            >
              + Add Event Info
            </ButtonV2>
          </div>
        </Header>

        <HelperText>
          Enable track and series info panels or add event-specific info to
          display in this event. Drag to reorder enabled panels. Track/series
          infos can be toggled; event infos can be deleted.
        </HelperText>

        {!hasAnyContent ? (
          <EmptyState>
            <EmptyIcon>
              <InfoPanelIcon />
            </EmptyIcon>
            <EmptyTitle>No info panels available</EmptyTitle>
            <EmptyDescription>
              This track and series do not have any info panels configured. You
              can add event-specific info panels using the button above.
            </EmptyDescription>
          </EmptyState>
        ) : (
          <>
            {/* Enabled items section */}
            {enabledItems.length > 0 && (
              <>
                <SectionHeader>
                  <SectionIcon>
                    <InfoPanelIcon />
                  </SectionIcon>
                  <SectionTitle>Active Info Panels</SectionTitle>
                  {enabledItems.length > 1 && (
                    <ReorderToggle
                      type="button"
                      $active={reorderMode}
                      onClick={() => setReorderMode(!reorderMode)}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <line x1="3" y1="12" x2="21" y2="12" />
                        <line x1="3" y1="18" x2="21" y2="18" />
                        <polyline points="7 3 3 6 7 9" />
                        <polyline points="17 15 21 18 17 21" />
                      </svg>
                      Reorder
                    </ReorderToggle>
                  )}
                </SectionHeader>

                {reorderMode ? (
                  <>
                    <ReorderHint>Drag panels to reorder them</ReorderHint>
                    <ListContainer>
                      <ReactSortable
                        tag={SortableContainer}
                        list={enabledItems}
                        setList={handleSetList}
                        animation={200}
                        handle=".reorder-drag-handle"
                        ghostClass="sortable-ghost"
                        chosenClass="sortable-chosen"
                      >
                        {enabledItems.map((item, index) => {
                          const info =
                            item.trackInfo || item.seriesInfo || item;
                          const name =
                            info.name ||
                            info.info?.name ||
                            'Untitled Info Panel';
                          const itemSource =
                            item.source ||
                            (item.isEventInfo ? 'event' : 'track');
                          return (
                            <div
                              key={`${item.source}-${item.id}`}
                              data-id={`${item.source}-${item.id}`}
                            >
                              <ReorderCard>
                                <ReorderDragStrip className="reorder-drag-handle">
                                  <DragIndicatorIcon width={18} height={18} />
                                </ReorderDragStrip>
                                <ReorderCardContent>
                                  <ReorderOrderNum>{index + 1}</ReorderOrderNum>
                                  <InfoIcon>
                                    {info.logo ? (
                                      <img src={info.logo} alt={name} />
                                    ) : (
                                      <InfoPanelIcon />
                                    )}
                                  </InfoIcon>
                                  <ReorderNameArea>
                                    <ReorderName>{name}</ReorderName>
                                    <ReorderSub>{itemSource}</ReorderSub>
                                  </ReorderNameArea>
                                </ReorderCardContent>
                              </ReorderCard>
                            </div>
                          );
                        })}
                      </ReactSortable>
                    </ListContainer>
                    <ReorderDoneButton
                      type="button"
                      onClick={() => setReorderMode(false)}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Done Reordering
                    </ReorderDoneButton>
                  </>
                ) : (
                  <ListContainer>
                    <ReactSortable
                      tag={SortableContainer}
                      list={enabledItems}
                      setList={handleSetList}
                      animation={200}
                      handle=".drag-handle"
                      ghostClass="sortable-ghost"
                      chosenClass="sortable-chosen"
                    >
                      {enabledItems.map((item, index) => (
                        <div
                          key={`${item.source}-${item.id}`}
                          data-id={`${item.source}-${item.id}`}
                        >
                          <InfoCardComponent
                            info={item.trackInfo || item.seriesInfo || item}
                            enabled={true}
                            order={index + 1}
                            source={
                              item.source ||
                              (item.isEventInfo ? 'event' : 'track')
                            }
                            seriesName={item.seriesName}
                            onToggle={
                              item.source === 'track' ||
                              item.source === 'series'
                                ? () => handleToggle(item.info?.id, item.source)
                                : null
                            }
                            onDelete={
                              item.source === 'event' || item.isEventInfo
                                ? () => handleDeleteEventInfo(item.id)
                                : null
                            }
                            onEdit={
                              item.source === 'event' || item.isEventInfo
                                ? () => handleEditEventInfo(item)
                                : null
                            }
                            expanded={expandedIds.has(
                              `${item.source}-${item.id}`
                            )}
                            onToggleExpand={() =>
                              handleToggleExpand(`${item.source}-${item.id}`)
                            }
                            onOpenMedia={openMedia}
                          />
                        </div>
                      ))}
                    </ReactSortable>
                  </ListContainer>
                )}
              </>
            )}

            {/* Disabled track items section */}
            {disabledTrackItems.length > 0 && (
              <>
                <SectionHeader
                  style={{ marginTop: enabledItems.length > 0 ? '24px' : 0 }}
                >
                  <SectionIcon>
                    <TrackIcon />
                  </SectionIcon>
                  <SectionTitle>Available from Track</SectionTitle>
                </SectionHeader>

                <ListContainer>
                  {disabledTrackItems.map((item) => (
                    <InfoCardComponent
                      key={`track-${item.id}`}
                      info={item.trackInfo || item}
                      enabled={false}
                      order={null}
                      source="track"
                      onToggle={() => handleToggle(item.info?.id, 'track')}
                      expanded={expandedIds.has(`track-${item.id}`)}
                      onToggleExpand={() =>
                        handleToggleExpand(`track-${item.id}`)
                      }
                      onOpenMedia={openMedia}
                    />
                  ))}
                </ListContainer>
              </>
            )}

            {/* Disabled series items section */}
            {disabledSeriesItems.length > 0 && (
              <>
                <SectionHeader
                  style={{
                    marginTop:
                      enabledItems.length > 0 || disabledTrackItems.length > 0
                        ? '24px'
                        : 0,
                  }}
                >
                  <SectionIcon>
                    <SeriesIcon />
                  </SectionIcon>
                  <SectionTitle>Available from Series</SectionTitle>
                </SectionHeader>

                <ListContainer>
                  {disabledSeriesItems.map((item) => (
                    <InfoCardComponent
                      key={`series-${item.id}`}
                      info={item.seriesInfo || item}
                      enabled={false}
                      order={null}
                      source="series"
                      seriesName={item.seriesName}
                      onToggle={() => handleToggle(item.info?.id, 'series')}
                      expanded={expandedIds.has(`series-${item.id}`)}
                      onToggleExpand={() =>
                        handleToggleExpand(`series-${item.id}`)
                      }
                      onOpenMedia={openMedia}
                    />
                  ))}
                </ListContainer>
              </>
            )}
          </>
        )}
      </Card>

      {/* Media Modal */}
      {modalMedia && <MediaModal media={modalMedia} onClose={closeMedia} />}

      {/* Add/Edit Event Info Modal */}
      <AddEventInfoModal
        isOpen={addInfoModalOpen}
        onClose={() => {
          setAddInfoModalOpen(false);
          setEditingEventInfo(null);
        }}
        onSave={handleSaveEventInfo}
        editingInfo={editingEventInfo}
      />
    </Container>
  );
};

// Individual info card component - Design 4 style
const InfoCardComponent = ({
  info,
  enabled,
  order: _order,
  source = 'track',
  seriesName,
  onToggle,
  onDelete,
  onEdit,
  expanded,
  onToggleExpand,
  onOpenMedia,
}) => {
  const contentTypes = getContentTypes(info);
  // For event infos, the name is directly on the info object
  // For track/series infos, it's nested under info.info.name
  const name = info.name || info.info?.name || 'Untitled Info Panel';
  const logo = info.logo;
  const hasContent = contentTypes.length > 0;
  const isEventInfo = source === 'event' || info.isEventInfo;
  const isSeriesInfo = source === 'series';

  return (
    <InfoCardWrapper disabled={!enabled}>
      <InfoCard>
        {/* Drag handle */}
        <DragHandle
          className={enabled ? 'drag-handle' : ''}
          disabled={!enabled}
          title={enabled ? 'Drag to reorder' : 'Enable to reorder'}
        >
          <DragIndicatorIcon width={20} height={20} />
        </DragHandle>

        {/* Icon/Logo */}
        <InfoIcon>
          {logo ? <img src={logo} alt={name} /> : <InfoPanelIcon />}
        </InfoIcon>

        {/* Name and Source Badge */}
        <InfoContent>
          <InfoName>{name}</InfoName>
        </InfoContent>

        {/* Source badge */}
        <SourceBadge
          source={isEventInfo ? 'event' : isSeriesInfo ? 'series' : 'track'}
        >
          {isEventInfo ? (
            <>
              <EventIcon />
              Event
            </>
          ) : isSeriesInfo ? (
            <>
              <SeriesIcon />
              {seriesName || 'Series'}
            </>
          ) : (
            <>
              <TrackIcon />
              Track
            </>
          )}
        </SourceBadge>

        {/* Content type pills */}
        {contentTypes.length > 0 && (
          <ContentPills>
            {contentTypes.map((type) => (
              <Pill key={type}>{contentTypeLabels[type]}</Pill>
            ))}
          </ContentPills>
        )}

        {/* Preview button */}
        {hasContent && (
          <PreviewButton
            type="button"
            expanded={expanded}
            onClick={onToggleExpand}
            title={expanded ? 'Hide details' : 'Show details'}
          >
            <ChevronIcon />
            Preview
          </PreviewButton>
        )}

        {/* Actions: Toggle for track, Edit+Delete for event */}
        <Actions>
          {isEventInfo ? (
            <>
              {onEdit && (
                <ButtonV2
                  variant="icon"
                  onClick={onEdit}
                  title="Edit info"
                  style={{ width: 32, height: 32, padding: 0 }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </ButtonV2>
              )}
              {onDelete && (
                <DeleteButton
                  type="button"
                  onClick={onDelete}
                  title="Delete info"
                >
                  <TrashIcon />
                </DeleteButton>
              )}
            </>
          ) : (
            <Toggle checked={enabled} onChange={onToggle} />
          )}
        </Actions>

        {/* Mobile line break */}
        <MobileBreak />
      </InfoCard>

      {/* Expandable details panel */}
      {hasContent && (
        <DetailsPanel expanded={expanded}>
          <DetailsContent>
            <DetailsGrid>
              {/* Text content */}
              {info.text && (
                <DetailItem style={{ gridColumn: '1 / -1' }}>
                  <DetailLabel>Text Content</DetailLabel>
                  <DetailText>{info.text}</DetailText>
                </DetailItem>
              )}

              {/* Link */}
              {info.link && (
                <DetailItem>
                  <DetailLabel>Link</DetailLabel>
                  <DetailLink
                    href={info.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkIcon />
                    {info.link}
                  </DetailLink>
                </DetailItem>
              )}

              {/* Media previews - now clickable */}
              {(info.logo ||
                info.image_url ||
                info.pdf_url ||
                info.video_url) && (
                <DetailItem>
                  <DetailLabel>Media (click to preview)</DetailLabel>
                  <MediaPreview>
                    {/* Logo */}
                    {info.logo && (
                      <ClickableMediaItem
                        type="button"
                        onClick={() => onOpenMedia('image', info.logo, 'Logo')}
                        title="Click to view logo"
                      >
                        <img src={info.logo} alt="Logo" />
                        <MediaLabel>Logo</MediaLabel>
                      </ClickableMediaItem>
                    )}

                    {/* Image */}
                    {info.image_url && (
                      <ClickableMediaItem
                        type="button"
                        onClick={() =>
                          onOpenMedia('image', info.image_url, 'Image')
                        }
                        title="Click to view image"
                      >
                        <img src={info.image_url} alt="Info panel media" />
                        <MediaLabel>Image</MediaLabel>
                      </ClickableMediaItem>
                    )}

                    {/* PDF */}
                    {info.pdf_url && (
                      <ClickablePdfPreview
                        type="button"
                        onClick={() => onOpenMedia('pdf', info.pdf_url)}
                        title="Click to view PDF"
                      >
                        <PdfIcon />
                        PDF
                      </ClickablePdfPreview>
                    )}

                    {/* Video */}
                    {info.video_url && (
                      <ClickableVideoPreview
                        type="button"
                        onClick={() => onOpenMedia('video', info.video_url)}
                        title="Click to play video"
                      >
                        <VideoIcon />
                        Video
                      </ClickableVideoPreview>
                    )}
                  </MediaPreview>
                </DetailItem>
              )}

              {/* No content message */}
              {!info.text &&
                !info.link &&
                !info.logo &&
                !info.image_url &&
                !info.pdf_url &&
                !info.video_url && (
                  <DetailItem>
                    <NoContent>No additional content configured</NoContent>
                  </DetailItem>
                )}
            </DetailsGrid>
          </DetailsContent>
        </DetailsPanel>
      )}
    </InfoCardWrapper>
  );
};

export default FanInfoTab;
